import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * OAuth callback page for social media connect (opened in popup).
 * Receives the auth code, exchanges it via the existing backend GraphQL mutations,
 * then posts the result back to the opener window.
 */
const SocialConnectCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing...');

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');

    if (error) {
      postResult(detectPlatform(state), null, error);
      setStatus('error');
      setMessage(`Authorization failed: ${error}`);
      return;
    }

    if (!code) {
      setStatus('error');
      setMessage('No authorization code received.');
      return;
    }

    const platform = detectPlatform(state);
    exchangeCode(platform, code)
      .then((result) => {
        setStatus('success');
        setMessage(`Connected ${platform} successfully!`);
        postResult(platform, result, null);
        setTimeout(() => window.close(), 1500);
      })
      .catch((err) => {
        setStatus('error');
        setMessage(err.message || 'Failed to connect');
        postResult(platform, null, err.message);
      });
  }, [searchParams]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="text-center p-8">
        {status === 'processing' && (
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
        )}
        {status === 'success' && <div className="text-green-600 text-4xl mb-4">✓</div>}
        {status === 'error' && <div className="text-red-600 text-4xl mb-4">✗</div>}
        <p className="text-gray-700">{message}</p>
      </div>
    </div>
  );
};

function postResult(platform: string, result: any, error: string | null) {
  window.opener?.postMessage({
    type: 'social-connect-result',
    platform,
    result,
    error,
  }, window.location.origin);
}

function detectPlatform(state: string | null): string {
  if (sessionStorage.getItem('naver_oauth_state') === state) return 'naverblog';
  if (sessionStorage.getItem('instagram_oauth_state') === state) return 'instagram';
  if (sessionStorage.getItem('google_oauth_state') === state) return 'youtube';
  return 'unknown';
}

async function exchangeCode(
  platform: string,
  code: string
): Promise<{ platform: string; username: string; email?: string }> {
  const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT;
  const apiKey = process.env.REACT_APP_GRAPHQL_API_KEY || 'da2-yirsmocw3fcdfkm35omcpgtley';
  const redirectUri = `${window.location.origin}/social-connect/callback`;

  if (!graphqlEndpoint) throw new Error('GraphQL endpoint not configured');

  if (platform === 'naverblog') {
    // Reuse the existing naverOAuth mutation
    const res = await fetch(graphqlEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
      body: JSON.stringify({
        query: `mutation NaverOAuth($input: NaverOAuthInput!) {
          naverOAuth(input: $input) { success message error }
        }`,
        variables: { input: { code, redirectUri } },
      }),
    });
    const data = await res.json();
    const result = data?.data?.naverOAuth;
    if (!result?.success) throw new Error(result?.error || 'Naver connect failed');
    const userInfo = JSON.parse(result.message);
    return { platform: 'naverblog', username: userInfo.name || userInfo.email, email: userInfo.email };
  }

  if (platform === 'instagram') {
    // Facebook/Instagram OAuth — exchange code for short-lived token, then get user info
    const fbAppId = process.env.REACT_APP_FACEBOOK_APP_ID || '';
    // Note: client_secret should be on backend. For now, return a placeholder.
    // TODO: Create a backend mutation for Instagram token exchange
    return { platform: 'instagram', username: `instagram_user_${code.slice(0, 8)}` };
  }

  if (platform === 'youtube') {
    // Google/YouTube OAuth — exchange code for token, get user info
    // Note: client_secret should be on backend. For now, return a placeholder.
    // TODO: Create a backend mutation for Google token exchange
    return { platform: 'youtube', username: `youtube_user_${code.slice(0, 8)}` };
  }

  throw new Error(`Unsupported platform: ${platform}`);
}

export default SocialConnectCallback;
